import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { UploadOptions, UploadProgress, UploadQueueItem, UploadQueueState, UploadSignal, UploadStatus } from '../file-library.model';
import { FileUploadService } from './file-upload.service';

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_COUNT = 3;
const DEFAULT_RETRY_DELAY = 2000; // 2 seconds
const DEFAULT_CONCURRENT_LIMIT = 3;

/**
 * Upload Queue Service
 * Manages a queue of file uploads with concurrency control, retry logic, and pause/resume capability.
 */
@Injectable({ providedIn: 'root' })
export class UploadQueueService {
	private readonly state$ = new BehaviorSubject<UploadQueueState>({
		items: [],
		isProcessing: false,
		isPaused: false,
		concurrentLimit: DEFAULT_CONCURRENT_LIMIT,
		activeUploads: 0
	});

	private readonly itemUpdated$ = new Subject<UploadQueueItem>();
	private readonly itemCompleted$ = new Subject<{ item: UploadQueueItem; result: UploadSignal | null }>();
	private readonly itemFailed$ = new Subject<{ item: UploadQueueItem; error: Error }>();

	private activeSubscriptions = new Map<string, Subscription>();

	constructor(private readonly fileUploadService: FileUploadService) {}

	/**
	 * Gets the current queue state as an observable
	 */
	getState(): Observable<UploadQueueState> {
		return this.state$.asObservable();
	}

	/**
	 * Gets the current queue state snapshot
	 */
	getStateSnapshot(): UploadQueueState {
		return this.state$.value;
	}

	/**
	 * Gets queue items observable
	 */
	getItems(): Observable<UploadQueueItem[]> {
		return this.state$.pipe(map(state => state.items));
	}

	/**
	 * Gets pending items count
	 */
	getPendingCount(): Observable<number> {
		return this.state$.pipe(map(state => state.items.filter(i => i.status === 'pending').length));
	}

	/**
	 * Gets item update notifications
	 */
	onItemUpdated(): Observable<UploadQueueItem> {
		return this.itemUpdated$.asObservable();
	}

	/**
	 * Gets item completion notifications
	 */
	onItemCompleted(): Observable<{ item: UploadQueueItem; result: UploadSignal | null }> {
		return this.itemCompleted$.asObservable();
	}

	/**
	 * Gets item failure notifications
	 */
	onItemFailed(): Observable<{ item: UploadQueueItem; error: Error }> {
		return this.itemFailed$.asObservable();
	}

	/**
	 * Adds a file to the upload queue
	 */
	addToQueue(file: File, directoryId?: string, options?: UploadOptions & { priority?: number }): string {
		const id = uuidv4();
		const item: UploadQueueItem = {
			id,
			file,
			directoryId,
			options,
			status: 'pending',
			progress: {
				fileId: id,
				filename: file.name,
				bytesUploaded: 0,
				totalBytes: file.size,
				percentage: 0,
				status: 'pending'
			},
			retryAttempts: 0,
			addedAt: Date.now(),
			priority: options?.priority ?? 0
		};

		this.updateState(state => ({
			...state,
			items: [...state.items, item].sort((a, b) => b.priority - a.priority)
		}));

		// Start processing if not paused
		if (!this.state$.value.isPaused) {
			this.processQueue();
		}

		return id;
	}

	/**
	 * Adds multiple files to the queue
	 */
	addBatchToQueue(files: File[], directoryId?: string, options?: UploadOptions & { priority?: number }): string[] {
		return files.map(file => this.addToQueue(file, directoryId, options));
	}

	/**
	 * Removes an item from the queue
	 */
	removeFromQueue(id: string): void {
		// Cancel if active
		this.cancelItem(id);

		this.updateState(state => ({
			...state,
			items: state.items.filter(i => i.id !== id)
		}));
	}

	/**
	 * Clears completed and failed items from the queue
	 */
	clearCompleted(): void {
		this.updateState(state => ({
			...state,
			items: state.items.filter(i => i.status !== 'complete' && i.status !== 'error' && i.status !== 'cancelled')
		}));
	}

	/**
	 * Clears all items from the queue
	 */
	clearAll(): void {
		// Cancel all active uploads
		for (const [id] of this.activeSubscriptions) {
			this.cancelItem(id);
		}

		this.updateState(state => ({
			...state,
			items: [],
			activeUploads: 0
		}));
	}

	/**
	 * Cancels a specific upload
	 */
	cancelItem(id: string): void {
		const sub = this.activeSubscriptions.get(id);
		if (sub) {
			sub.unsubscribe();
			this.activeSubscriptions.delete(id);
		}

		this.fileUploadService.cancelUpload(id);

		this.updateItemStatus(id, 'cancelled');
		this.updateState(state => ({
			...state,
			activeUploads: Math.max(0, state.activeUploads - 1)
		}));

		// Continue processing remaining items
		this.processQueue();
	}

	/**
	 * Pauses queue processing
	 */
	pause(): void {
		// Set paused flag FIRST to prevent processQueue from starting new uploads
		this.updateState(state => ({
			...state,
			isPaused: true
		}));

		// Get uploading item IDs from fresh state
		const uploadingIds = this.state$.value.items
			.filter(i => i.status === 'uploading')
			.map(i => i.id);

		// Cancel each upload and set to paused
		// Note: cancelItem calls processQueue, but isPaused=true prevents new uploads
		for (const id of uploadingIds) {
			const sub = this.activeSubscriptions.get(id);
			if (sub) {
				sub.unsubscribe();
				this.activeSubscriptions.delete(id);
			}
			this.fileUploadService.cancelUpload(id);
			// Set directly to 'paused' instead of going through 'cancelled'
			this.updateItemStatus(id, 'paused');
		}

		// Decrement active uploads count in one update
		this.updateState(state => ({
			...state,
			activeUploads: Math.max(0, state.activeUploads - uploadingIds.length)
		}));
	}

	/**
	 * Resumes queue processing
	 */
	resume(): void {
		// Reset paused items to pending
		const state = this.state$.value;
		state.items
			.filter(i => i.status === 'paused')
			.forEach(item => {
				this.updateItemStatus(item.id, 'pending');
			});

		this.updateState(state => ({
			...state,
			isPaused: false
		}));

		this.processQueue();
	}

	/**
	 * Retries a failed upload
	 */
	retryItem(id: string): void {
		const item = this.state$.value.items.find(i => i.id === id);
		if (item && (item.status === 'error' || item.status === 'cancelled')) {
			this.updateItem(id, {
				status: 'pending',
				retryAttempts: 0,
				progress: {
					...item.progress,
					status: 'pending',
					error: undefined
				}
			});
			this.processQueue();
		}
	}

	/**
	 * Retries all failed uploads
	 */
	retryAllFailed(): void {
		const state = this.state$.value;
		state.items.filter(i => i.status === 'error').forEach(item => this.retryItem(item.id));
	}

	/**
	 * Sets the concurrent upload limit
	 */
	setConcurrentLimit(limit: number): void {
		this.updateState(state => ({
			...state,
			concurrentLimit: Math.max(1, limit)
		}));
		this.processQueue();
	}

	/**
	 * Processes the upload queue
	 */
	private processQueue(): void {
		const state = this.state$.value;

		if (state.isPaused) {
			return;
		}

		const availableSlots = state.concurrentLimit - state.activeUploads;
		if (availableSlots <= 0) {
			return;
		}

		const pendingItems = state.items.filter(i => i.status === 'pending').slice(0, availableSlots);

		if (pendingItems.length === 0) {
			if (state.activeUploads === 0 && state.isProcessing) {
				this.updateState(s => ({ ...s, isProcessing: false }));
			}
			return;
		}

		if (!state.isProcessing) {
			this.updateState(s => ({ ...s, isProcessing: true }));
		}

		pendingItems.forEach(item => this.startUpload(item));
	}

	/**
	 * Starts uploading an item
	 */
	private startUpload(item: UploadQueueItem): void {
		// RACE CONDITION FIX: Re-verify we have capacity before starting
		// This prevents multiple concurrent processQueue calls from exceeding concurrentLimit
		const currentState = this.state$.value;
		if (currentState.activeUploads >= currentState.concurrentLimit) {
			// No capacity - item stays pending and will be picked up later
			return;
		}

		// Also verify the item is still pending (could have been cancelled)
		const currentItem = currentState.items.find(i => i.id === item.id);
		if (!currentItem || currentItem.status !== 'pending') {
			return;
		}

		this.updateItemStatus(item.id, 'uploading');
		this.updateState(state => ({
			...state,
			activeUploads: state.activeUploads + 1
		}));

		const subscription = this.fileUploadService
			.uploadFile(item.file, item.directoryId, item.options)
			.pipe(
				tap(result => {
					this.updateItemStatus(item.id, 'complete');
					this.itemCompleted$.next({ item, result });
				}),
				catchError((error: Error) => {
					return this.handleUploadError(item, error);
				})
			)
			.subscribe({
				complete: () => {
					this.activeSubscriptions.delete(item.id);
					this.updateState(state => ({
						...state,
						activeUploads: Math.max(0, state.activeUploads - 1)
					}));
					this.processQueue();
				},
				error: () => {
					this.activeSubscriptions.delete(item.id);
					this.updateState(state => ({
						...state,
						activeUploads: Math.max(0, state.activeUploads - 1)
					}));
					this.processQueue();
				}
			});

		this.activeSubscriptions.set(item.id, subscription);
	}

	/**
	 * Handles upload error with retry logic
	 */
	private handleUploadError(item: UploadQueueItem, error: Error): Observable<null> {
		const maxRetries = item.options?.retryCount ?? DEFAULT_RETRY_COUNT;
		const retryDelay = item.options?.retryDelay ?? DEFAULT_RETRY_DELAY;
		const isCancelled = (error as Error & { isCancelled?: boolean })?.isCancelled;

		if (isCancelled) {
			this.updateItemStatus(item.id, 'cancelled');
			return of(null);
		}

		if (item.retryAttempts < maxRetries) {
			// Schedule retry
			const newAttempts = item.retryAttempts + 1;
			this.updateItem(item.id, {
				retryAttempts: newAttempts,
				status: 'pending',
				progress: {
					...item.progress,
					status: 'pending',
					error: `Retry ${newAttempts}/${maxRetries}: ${error.message}`
				}
			});

			// Delay before retry
			timer(retryDelay * newAttempts)
				.pipe(take(1))
				.subscribe(() => {
					this.processQueue();
				});

			return of(null);
		}

		// Max retries reached
		this.updateItem(item.id, {
			status: 'error',
			progress: {
				...item.progress,
				status: 'error',
				error: error.message
			}
		});
		this.itemFailed$.next({ item, error });

		return of(null);
	}

	/**
	 * Updates the state
	 */
	private updateState(updater: (state: UploadQueueState) => UploadQueueState): void {
		this.state$.next(updater(this.state$.value));
	}

	/**
	 * Updates an item's status
	 */
	private updateItemStatus(id: string, status: UploadStatus): void {
		const item = this.state$.value.items.find(i => i.id === id);
		if (!item) {
			console.warn(`UploadQueueService: Cannot update status for non-existent item: ${id}`);
			return;
		}

		this.updateItem(id, {
			status,
			progress: { ...item.progress, status }
		});
	}

	/**
	 * Updates an item in the queue
	 */
	private updateItem(id: string, updates: Partial<UploadQueueItem>): void {
		this.updateState(state => ({
			...state,
			items: state.items.map(item => {
				if (item.id === id) {
					const updated = { ...item, ...updates };
					this.itemUpdated$.next(updated);
					return updated;
				}
				return item;
			})
		}));
	}
}
